import { useEffect } from "react";
import { useParams } from "react-router-dom";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Edit, Loader2, MoreHorizontal, Plus, Save } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Document } from "./types";

import { BusinessPlanView } from "./BusinessPlanView";
import { FinancialForecastView } from "./FinancialForecastView";
import { StrategicPlanView } from "./StrategicPlanView";
import { OrgChartView } from "./OrgChartView";
import { BoardStrategyView } from "./BoardStrategyView";
import { BudgetTemplateView } from "./BudgetTemplateView";
import { TechArchitectureView } from "./TechArchitectureView";
import { DigitalTransformationPlanView } from "./DigitalTransformationPlanView";
import { ProcessOptimizationView } from "./ProcessOptimizationView";
import { OperationsManualView } from "./OperationsManualView";
import { ProductRoadmapView } from "./ProductRoadmapView";
import { UserResearchPlanView } from "./UserResearchPlanView";
import { ConsultingAgreementView } from "./ConsultingAgreementView";
import { MediaReleaseView } from "./MediaReleaseView";
import { NonDisclosureView } from "./NonDisclosureView";
import { ContractView } from "./ContractView";
import { InvoiceGeneratorView } from "./InvoiceGeneratorView";
import { ProposalGeneratorView } from "./ProposalGeneratorView";
import { RiskMitigationView } from "./RiskMitigationView";

interface DocumentDetailEditorProps {
  documentId?: string;
  onSave?: (content: any) => Promise<void>;
  isSaving?: boolean;
}

export function DocumentDetailEditor({ documentId: propDocumentId, onSave, isSaving }: DocumentDetailEditorProps) {
  const { id: paramDocumentId } = useParams<{ id: string }>();
  const documentId = propDocumentId || paramDocumentId;
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const {
    data: document,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ["document", documentId],
    queryFn: async () => {
      if (!documentId) throw new Error("Document ID is required");

      console.log("Fetching document with ID:", documentId);

      const { data, error } = await supabase
        .from("documents")
        .select("*")
        .eq("id", documentId)
        .single();

      if (error) {
        console.error("Error fetching document:", error);
        toast({
          title: "Error fetching document",
          description: error.message,
          variant: "destructive",
        });
        throw error;
      }

      console.log("Document fetched:", data);
      return data as Document;
    },
    enabled: !!documentId,
  });

  const updateMutation = useMutation({
    mutationFn: async (content: any) => {
      if (!documentId) throw new Error("Document ID is required");

      const { error } = await supabase
        .from("documents")
        .update({ 
          content,
          updated_at: new Date().toISOString()
        })
        .eq("id", documentId);

      if (error) throw error;
    },
    onSuccess: () => {
      toast({
        title: "Success",
        description: "Document updated successfully.",
      });
      refetch();
    },
    onError: (error: any) => {
      console.error("Error updating document:", error);
      toast({
        title: "Error",
        description: "Failed to update document. Please try again.",
        variant: "destructive",
      });
    },
  });

  const handleContentChange = (newContent: any) => {
    updateMutation.mutate(newContent);
  };

  const renderDocumentView = () => {
    if (!document) return null;

    console.log("Rendering document view for type:", document.type);
    console.log("Document content:", document.content);

    // Ensure content is treated as an object, not a string
    const content = typeof document.content === 'string' 
      ? JSON.parse(document.content) 
      : (document.content || {});

    console.log("Processed content:", content);

    // Handle both database enum values and extended DocumentType values
    const docType = document.type as string;

    switch (docType) {
      case 'business_plan':
        return <BusinessPlanView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'financial_forecast':
        return <FinancialForecastView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'strategic_plan':
        return <StrategicPlanView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'org_chart':
        return <OrgChartView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'board_strategy':
        return <BoardStrategyView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'budget_template':
        return <BudgetTemplateView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'tech_architecture':
        return <TechArchitectureView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'digital_transformation_plan':
        return <DigitalTransformationPlanView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'process_optimization':
        return <ProcessOptimizationView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'operations_manual':
        return <OperationsManualView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'product_roadmap':
        return <ProductRoadmapView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'user_research_plan':
        return <UserResearchPlanView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'consulting_agreement':
        return <ConsultingAgreementView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'media_release':
        return <MediaReleaseView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'nda_agreement':
        return <NonDisclosureView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'service_contract':
        return <ContractView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'invoice_generator':
        return <InvoiceGeneratorView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'proposal_generator':
        return <ProposalGeneratorView content={content} documentId={document.id} onUpdate={refetch} />;
      case 'risk_mitigation_plan':
        return <RiskMitigationView content={content} documentId={document.id} onUpdate={refetch} />;
      default:
        console.log("Unknown document type:", docType);
        return (
          <div className="text-center py-8">
            <p className="text-gray-500">Document type "{docType}" not supported yet.</p>
            <p className="text-sm text-gray-400 mt-2">Please contact support if you need this document type.</p>
          </div>
        );
    }
  };

  if (!documentId) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-500">No document ID provided.</p>
      </div>
    );
  }

  return (
    <div className="w-full">
      {isLoading ? (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="mr-2 h-4 w-4 animate-spin" />
          Loading document...
        </div>
      ) : document ? (
        <div className="space-y-6">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl font-semibold text-gray-900">{document.title}</h2>
              <p className="text-sm text-gray-500">Document ID: {document.id}</p>
            </div>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="h-8 w-8 p-0">
                  <span className="sr-only">Open menu</span>
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuLabel>Actions</DropdownMenuLabel>
                <DropdownMenuItem onClick={() => refetch()}>
                  <Edit className="mr-2 h-4 w-4" />
                  Refresh
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          
          <Card className="w-full">
            <CardContent className="p-6">
              {renderDocumentView()}
            </CardContent>
          </Card>
        </div>
      ) : (
        <div className="text-center py-8">
          <p className="text-gray-500">Document not found.</p>
        </div>
      )}
    </div>
  );
}
